import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr, num, pnlColor } from "@/lib/format";
import { LayoutDashboard, TrendingUp, TrendingDown, CalendarClock, Wallet } from "lucide-react";
import { visibleInterval } from "@/lib/poll";

function Stat({ label, value, sub, cls = "", testid }) {
  return (
    <div className="term-panel rounded-sm p-4" data-testid={testid}>
      <div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-xl font-semibold mono mt-1 ${cls}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function LegacyDashboard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    const load = () => api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    load();
    return visibleInterval(load, 5000);
  }, []);

  if (!d) return <div className="p-6 text-slate-500">Loading legacy dashboard…</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><LayoutDashboard className="h-6 w-6 text-[#2563EB]" /> Legacy Dashboard
          <span className="text-xs font-normal ml-2 px-2 py-0.5 rounded-sm bg-[#94A3B8]/15 text-slate-300">SIMULATION / DEV · MOCK DATA</span>
        </h1>
        <div className="flex items-center gap-2 text-xs">
          <span data-testid="automation-badge" className={`px-2 py-1 rounded-sm font-bold ${d.automation_state === "RUNNING" ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>
            AUTOMATION: {d.automation_state}
          </span>
        </div>
      </div>

      {!d.broker.connected && (
        <div className="term-panel rounded-sm p-4 text-sm text-[#FFCC00] flex items-center justify-between">
          <span>Broker not connected — connect a session to sync funds and enable automation.</span>
          <button onClick={() => nav("/broker")} className="text-xs font-semibold px-3 py-1.5 rounded-sm bg-[#2563EB]">Go to Broker Session</button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Available Funds" value={inr(d.available_funds)} testid="stat-available-funds" />
        <Stat label="Used Margin" value={inr(d.used_margin)} sub={`${d.margin_util_pct}% util`} testid="stat-used-margin" />
        <Stat label="Deployed Capital" value={inr(d.deployed_capital)} testid="stat-deployed" />
        <Stat label="Combined Live P&L" value={inr(d.combined_live_pnl)} cls={pnlColor(d.combined_live_pnl)} testid="stat-live-pnl" />
        <Stat label="Realized (day)" value={inr(d.daily_realized_pnl)} cls={pnlColor(d.daily_realized_pnl)} testid="stat-realized" />
        <Stat label="Max Configured Risk" value={inr(d.max_risk)} testid="stat-max-risk" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Active Strategies" value={num(d.active_strategies, 0)} />
        <Stat label="Total Matched Lots" value={num(d.total_matched_lots, 0)} />
        <Stat label="Total Funds" value={inr(d.total_funds)} />
        <Stat label="Free Margin" value={inr(d.broker.free_margin)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="term-panel rounded-sm p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Upcoming Expiries</div>
          <div className="space-y-2">
            {d.upcoming_expiries.map((e) => (
              <div key={e.category} className="flex items-center justify-between text-sm">
                <span className="capitalize text-slate-300">{e.category}-month</span>
                <span className="mono text-slate-100">{e.date}</span>
                <span className={`mono text-xs px-2 py-0.5 rounded-sm ${e.days_to_expiry <= 5 ? "bg-[#FF3B30]/15 text-[#FF6b62]" : "bg-[#1A1D24] text-slate-400"}`}>{e.days_to_expiry}d</span>
              </div>
            ))}
          </div>
        </div>

        <div className="term-panel rounded-sm p-5 lg:col-span-2">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-2"><Wallet className="h-4 w-4" /> Margin Utilization</div>
          <div className="h-3 w-full bg-[#0A0C10] rounded-sm overflow-hidden border border-[#2A2F3A]">
            <div className={`h-full ${d.margin_util_pct > 70 ? "bg-[#FF3B30]" : d.margin_util_pct > 50 ? "bg-[#FFCC00]" : "bg-[#2563EB]"}`} style={{ width: `${Math.min(100, d.margin_util_pct)}%` }} />
          </div>
          <div className="flex justify-between text-[11px] text-slate-500 mt-2 mono">
            <span>Used {inr(d.used_margin)}</span>
            <span>{d.margin_util_pct}%</span>
            <span>Total {inr(d.total_funds)}</span>
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            {d.combined_live_pnl >= 0 ? <TrendingUp className="h-4 w-4 text-[#00C805]" /> : <TrendingDown className="h-4 w-4 text-[#FF3B30]" />}
            Combined unrealized P&L across all open matched positions is <span className={pnlColor(d.combined_live_pnl)}>{inr(d.combined_live_pnl)}</span>. Estimated, not guaranteed.
          </div>
        </div>
      </div>
    </div>
  );
}
